import { createSignal, createMemo, Show, For, type Component } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { formatCell, isModuleUnavailableError } from "@/shared/format-utils";
import { Skeleton } from "@/components/skeleton";
import { Donut } from "./lib/donut";
import { PanelKpi, PanelKpiGrid, RefreshIndicator } from "@/shared/panel-primitives";
import { usePanelRefresh } from "@/shared/use-panel-refresh";
import { registerPanel } from "@/panel-registry";

interface Mailbox {
  DisplayName?: string;
  Alias?: string;
  UserPrincipalName?: string;
  RecipientTypeDetails?: string;
  PrimarySmtpAddress?: string;
  ProhibitSendQuota?: string;
  HiddenFromAddressListsEnabled?: boolean;
}

interface QueueItem {
  Identity?: string;
  DeliveryType?: string;
  Status?: string;
  MessageCount?: number;
  NextHopDomain?: string;
}

interface ListResult<T> {
  rows: T[];
  limit?: number;
}

interface MailboxDetailResult {
  identity?: string;
  mailbox?: Record<string, unknown>;
}

function isModuleError(err: unknown): boolean {
  return isModuleUnavailableError(String(err instanceof Error ? err.message : (err ?? "")));
}

const ExchangePanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [filter, setFilter] = createSignal("");
  const [limit, setLimit] = createSignal(100);
  const [detailIdentity, setDetailIdentity] = createSignal("");
  const [detailRequested, setDetailRequested] = createSignal("");
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const mailboxesQuery = createToolQuery<ListResult<Mailbox>>(() => ({
    tool: "exchange_mailboxes_list",
    params: { filter: filter(), limit: limit() },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const queueQuery = createToolQuery<ListResult<QueueItem>>(() => ({
    tool: "exchange_queues_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const mailboxesRefresh = usePanelRefresh({
    query: () => ({
      isFetching: mailboxesQuery.isFetching,
      refetch: () => mailboxesQuery.refetch(),
      dataUpdatedAt: mailboxesQuery.dataUpdatedAt,
      error: mailboxesQuery.error,
    }),
  });
  const queueRefresh = usePanelRefresh({
    query: () => ({
      isFetching: queueQuery.isFetching,
      refetch: () => queueQuery.refetch(),
      dataUpdatedAt: queueQuery.dataUpdatedAt,
      error: queueQuery.error,
    }),
  });

  const detailQuery = createToolQuery<MailboxDetailResult>(() => ({
    tool: "exchange_mailbox_get",
    params: { identity: detailRequested() },
    enabled: enabled() && detailRequested().length > 0,
    refetchInterval: 0,
  }));

  async function runMailboxAction(
    identity: string,
    actionId: "hide" | "unhide" | "set-quota" | "disable",
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    if (!identity) {
      setActionStatus({ text: "Identite mailbox manquante.", level: "warn" });
      return;
    }
    let quotaGB: number | null = null;
    if (actionId === "set-quota") {
      const raw = window.prompt(`Nouveau quota (GB, 1-1000) pour ${identity}`, "50");
      if (raw == null) return;
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 1 || n > 1000) {
        setActionStatus({ text: "Quota invalide.", level: "danger" });
        return;
      }
      quotaGB = n;
    }
    const ok = await confirm.confirm({
      title: `${label} - ${identity}`,
      body: `Confirmer l'action Exchange "${label}" sur ${identity}${quotaGB != null ? ` (quota ${quotaGB}GB)` : ""} ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? identity : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label}...`, level: "info" });
    try {
      const params: Record<string, unknown> = { identity, action: actionId };
      if (quotaGB != null) params.quotaGB = quotaGB;
      await tools.call("exchange_mailbox_action", params, {
        userConfirmedAt: new Date().toISOString(),
      });
      setActionStatus({ text: `Action ${label} sur ${identity} reussie.`, level: "info" });
      await mailboxesQuery.refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionStatus({
        text: isModuleError(err) ? "Exchange Management non detecte." : `Erreur action: ${msg}`,
        level: "danger",
      });
    }
  }

  async function runQueueAction(
    queueIdentity: string,
    actionId: "retry" | "suspend" | "resume",
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    if (!queueIdentity) {
      setActionStatus({ text: "Identite queue manquante.", level: "warn" });
      return;
    }
    const ok = await confirm.confirm({
      title: `${label} - ${queueIdentity}`,
      body: `Confirmer l'action queue Exchange "${label}" sur ${queueIdentity} ?`,
      level,
      confirmLabel: label,
      entityName: queueIdentity,
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label}...`, level: "info" });
    try {
      await tools.call(
        "exchange_queue_action",
        { queueIdentity, action: actionId },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Action ${label} sur ${queueIdentity} reussie.`, level: "info" });
      await queueQuery.refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionStatus({
        text: isModuleError(err) ? "Exchange Management non detecte." : `Erreur action: ${msg}`,
        level: "danger",
      });
    }
  }

  const mailboxesLoading = () => mailboxesQuery.isFetching && !mailboxesQuery.data;
  const queueLoading = () => queueQuery.isFetching && !queueQuery.data;

  // ─── KPI cards — calculés depuis les données déjà chargées ─────────────
  const kpis = createMemo(() => {
    const mailboxes = mailboxesQuery.data?.rows ?? [];
    const queue = queueQuery.data?.rows ?? [];
    const hidden = mailboxes.filter((m) => m.HiddenFromAddressListsEnabled === true).length;
    let queuedMessages = 0;
    let stuckQueues = 0;
    for (const q of queue) {
      const n = Number(q.MessageCount);
      if (Number.isFinite(n) && n > 0) queuedMessages += n;
      const st = String(q.Status ?? "").toLowerCase();
      if (st === "suspended" || st === "retry") stuckQueues += 1;
    }
    return {
      mailboxes: mailboxes.length,
      hidden,
      visible: mailboxes.length - hidden,
      queues: queue.length,
      queuedMessages,
      stuckQueues,
    };
  });

  const kpiCards = (loading: () => boolean) => (
    <Show
      when={!loading()}
      fallback={
        <PanelKpiGrid>
          <For each={[0, 1, 2, 3]}>
            {() => (
              <div class="panel-kpi">
                <div class="panel-kpi__body">
                  <Skeleton variant="line" width="60%" height="11px" />
                  <Skeleton variant="line" width="40%" height="22px" />
                </div>
              </div>
            )}
          </For>
        </PanelKpiGrid>
      }
    >
      <PanelKpiGrid>
        <PanelKpi
          label="Mailboxes"
          value={kpis().mailboxes}
          sub={`${kpis().hidden} cachee(s)`}
          intent="info"
          leading={
            <Donut
              size={48}
              thickness={8}
              segments={[
                { label: "Visibles", value: kpis().visible, color: "var(--accent-info, #4f94f4)" },
                { label: "Cachees", value: kpis().hidden, color: "var(--accent-warn, #d89614)" },
              ]}
            />
          }
        />
        <PanelKpi
          label="Messages en queue"
          value={kpis().queuedMessages}
          sub={`${kpis().queues} file(s)`}
          intent={kpis().queuedMessages > 0 ? "warn" : "ok"}
        />
        <PanelKpi
          label="Files bloquees"
          value={kpis().stuckQueues}
          sub="suspended / retry"
          intent={kpis().stuckQueues > 0 ? "warn" : "muted"}
        />
      </PanelKpiGrid>
    </Show>
  );

  const mailboxColumns: Column<Mailbox>[] = [
    { accessorKey: "DisplayName", header: "DisplayName" },
    { accessorKey: "Alias", header: "Alias" },
    { accessorKey: "UserPrincipalName", header: "UserPrincipalName" },
    { accessorKey: "RecipientTypeDetails", header: "RecipientTypeDetails" },
    { accessorKey: "PrimarySmtpAddress", header: "PrimarySmtpAddress" },
    { accessorKey: "ProhibitSendQuota", header: "ProhibitSendQuota" },
    {
      accessorKey: "HiddenFromAddressListsEnabled",
      header: "Hidden",
      cell: (info) => formatCell(info.getValue()),
    },
  ];

  const queueColumns: Column<QueueItem>[] = [
    { accessorKey: "Identity", header: "Identity" },
    { accessorKey: "DeliveryType", header: "DeliveryType" },
    { accessorKey: "Status", header: "Status" },
    { accessorKey: "MessageCount", header: "MessageCount" },
    { accessorKey: "NextHopDomain", header: "NextHopDomain" },
  ];

  const moduleErrorBanner = () => {
    const err = mailboxesQuery.error || queueQuery.error || detailQuery.error;
    if (err && isModuleError(err)) {
      const msg = err instanceof Error ? err.message : String(err);
      return (
        <div class="panel-data-empty panel-data-empty--danger">
          <strong>Exchange Management non detecte</strong>
          <br />
          Lancer <code>Connect-ExchangeOnline</code> sur le poste (ou activer le snap-in on-prem) puis reessayer.
          <br />
          <span style={{ opacity: 0.7 }}>Detail: {msg}</span>
        </div>
      );
    }
    return null;
  };

  const filterToolbar = (
    <>
      <input
        type="text"
        class="panel-filter-input"
        placeholder="Filtre (nom/alias/SMTP)..."
        spellcheck={false}
        value={filter()}
        onInput={(e) => setFilter(e.currentTarget.value)}
      />
      <input
        type="number"
        class="panel-filter-input panel-filter-input--narrow"
        min="1"
        max="1000"
        title="Limite"
        value={limit()}
        onInput={(e) => {
          const n = Number(e.currentTarget.value);
          if (Number.isFinite(n) && n > 0) setLimit(n);
        }}
      />
      <button
        type="button"
        class="btn btn--xs btn--info"
        onClick={() => {
          mailboxesQuery.refetch();
        }}
      >
        Rechercher
      </button>
      <RefreshIndicator state={mailboxesRefresh} />
    </>
  );

  const subtabs: SubTab[] = [
    {
      id: "mailboxes",
      label: "Mailboxes",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">{filterToolbar}</div>
          {kpiCards(mailboxesLoading)}
          <Show
            when={moduleErrorBanner()}
            fallback={
              <Show when={!mailboxesLoading()} fallback={<Skeleton variant="table" rows={8} />}>
                <DataTable<Mailbox>
                  rows={mailboxesQuery.data?.rows ?? []}
                  columns={mailboxColumns}
                  rowActions={(r) => {
                    const upn = r.UserPrincipalName ?? r.PrimarySmtpAddress ?? r.Alias ?? "";
                    return (
                      <>
                        <button
                          type="button"
                          class="btn btn--xs btn--warn"
                          disabled={r.HiddenFromAddressListsEnabled === true}
                          onClick={() => runMailboxAction(upn, "hide", "Cacher", "warn")}
                        >
                          Cacher
                        </button>
                        <button
                          type="button"
                          class="btn btn--xs btn--ghost"
                          disabled={r.HiddenFromAddressListsEnabled === false}
                          onClick={() => runMailboxAction(upn, "unhide", "Reveler", "info")}
                        >
                          Reveler
                        </button>
                        <button
                          type="button"
                          class="btn btn--xs btn--warn"
                          onClick={() => runMailboxAction(upn, "set-quota", "Set Quota", "warn")}
                        >
                          Set Quota
                        </button>
                        <button
                          type="button"
                          class="btn btn--xs btn--danger"
                          onClick={() => runMailboxAction(upn, "disable", "Disable", "danger")}
                        >
                          Disable
                        </button>
                      </>
                    );
                  }}
                />
              </Show>
            }
          >
            {moduleErrorBanner()}
          </Show>
        </>
      ),
    },
    {
      id: "queue",
      label: "Queue",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => queueQuery.refetch()}>
              Rafraichir
            </button>
            <RefreshIndicator state={queueRefresh} />
          </div>
          {kpiCards(queueLoading)}
          <Show
            when={moduleErrorBanner()}
            fallback={
              <Show when={!queueLoading()} fallback={<Skeleton variant="table" rows={8} />}>
                <DataTable<QueueItem>
                  rows={queueQuery.data?.rows ?? []}
                  columns={queueColumns}
                  rowActions={(r) => (
                    <>
                      <button
                        type="button"
                        class="btn btn--xs btn--warn"
                        onClick={() => runQueueAction(r.Identity ?? "", "retry", "Retry", "warn")}
                      >
                        Retry
                      </button>
                      <button
                        type="button"
                        class="btn btn--xs btn--warn"
                        onClick={() => runQueueAction(r.Identity ?? "", "suspend", "Suspend", "warn")}
                      >
                        Suspend
                      </button>
                      <button
                        type="button"
                        class="btn btn--xs btn--ghost"
                        onClick={() => runQueueAction(r.Identity ?? "", "resume", "Resume", "info")}
                      >
                        Resume
                      </button>
                    </>
                  )}
                />
              </Show>
            }
          >
            {moduleErrorBanner()}
          </Show>
        </>
      ),
    },
    {
      id: "detail",
      label: "Mailbox detail",
      render: () => {
        const mailbox = () => detailQuery.data?.mailbox ?? null;
        const keys = (): string[] => {
          const m = mailbox();
          return m ? Object.keys(m).sort() : [];
        };
        return (
          <>
            <div class="panel-toolbar panel-toolbar--filter">
              <input
                type="text"
                class="panel-filter-input"
                placeholder="UPN ou alias..."
                spellcheck={false}
                value={detailIdentity()}
                onInput={(e) => setDetailIdentity(e.currentTarget.value)}
              />
              <button
                type="button"
                class="btn btn--xs btn--info"
                disabled={!detailIdentity().trim()}
                onClick={() => setDetailRequested(detailIdentity().trim())}
              >
                Charger
              </button>
            </div>
            <Show
              when={moduleErrorBanner()}
              fallback={
                <Show
                  when={mailbox()}
                  fallback={<div class="panel-data-empty">Saisissez une identite et cliquez sur Charger.</div>}
                >
                  {(mb) => (
                    <table class="panel-data-table">
                      <tbody>
                        <For each={keys()}>
                          {(k) => (
                            <tr>
                              <th style={{ "text-align": "left" }}>{k}</th>
                              <td>{formatCell(mb()[k])}</td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  )}
                </Show>
              }
            >
              {moduleErrorBanner()}
            </Show>
          </>
        );
      },
    },
  ];

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (mailboxesQuery.isFetching || queueQuery.isFetching || detailQuery.isFetching)
      return { text: "Chargement...", level: "info" };
    const err = mailboxesQuery.error || queueQuery.error || detailQuery.error;
    if (err) {
      if (isModuleError(err)) return { text: "Exchange Management non detecte.", level: "danger" };
      const msg = err instanceof Error ? err.message : String(err);
      return { text: `Erreur: ${msg}`, level: "danger" };
    }
    return undefined;
  };

  return <PanelLayout title="Exchange" subtabs={subtabs} status={status()} />;
};

registerPanel("exchange", ExchangePanel);
export default ExchangePanel;
